import {
  Box,
  type SxProps,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  type ChangeEvent,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInputRefMerger } from '@/hooks/useRefMerger';
import { useTranslation } from 'react-i18next';
import React from 'react';

import { isHebrewFont } from '@/lib/isHebrewFont';
import { autoFillColor } from '@/config/inputConfig';

type BaseInputProps = {
  label?: string;
  big?: boolean;
  large?: boolean;
  medium?: boolean;
  small?: boolean;
  compact?: boolean;
  fullWidth?: boolean;
  smallLabel?: boolean;
  marginRight?: boolean;
  blueBorder?: boolean;
  marginBottom?: number;
  labelMarginBottom?: string;
  maxWidth?: number;
  name?: string;
  changeColorOnFocus?: boolean;
  textColor?: string;
  blur?: (value: string) => void;
  focus?: () => void;
  view?: boolean;
  isTextDirectionRtl?: boolean;
  valueForNumberChanging?: number;
  helperText: string;
  value: string | number;
  disabled?: boolean;
  error?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  type?: string;
  sx?: SxProps;
  sxLabel?: SxProps;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  autoHeight?: boolean;
};

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      label,
      big,
      medium,
      large,
      small,
      compact,
      error,
      helperText,
      fullWidth,
      smallLabel,
      // blueBorder,
      maxWidth,
      marginRight,
      marginBottom,
      labelMarginBottom,
      changeColorOnFocus,
      textColor,
      view,
      blur,
      focus,
      isTextDirectionRtl,
      // valueForNumberChanging,
      startAdornment,
      endAdornment,
      sxLabel,
      autoHeight,
      onClick,
      ...props
    },
    ref
  ) => {
    const { palette, direction, shape, spacing } = useTheme();
    const isHebrew = isHebrewFont(props?.value?.toString() ?? '');
    const isRtl = direction === 'rtl';
    const { t } = useTranslation();
    const [focused, setFocused] = useState(false);

    const handleFocus = () => {
      if (changeColorOnFocus) {
        setFocused(true);
      }
      if (focus) {
        focus();
      }
    };

    const internalRef = useRef<HTMLInputElement>(null);

    // Merge external ref with internal ref when not in view mode
    const mergedRef = useInputRefMerger(view ? undefined : ref, internalRef);

    const styles = useMemo(
      () => ({
        container: {
          mt: compact ? spacing(0.375) : spacing(2.5), // 3px : 20px
          maxWidth: maxWidth ? `${maxWidth / 16}rem` : 'auto',
          width: fullWidth ? '100%' : 'auto',
          mr: marginRight ? spacing(2.5) : 0, // 20px
          mb: marginBottom ? `${marginBottom / 16}rem` : 0,
        },
        label: {
          fontSize: smallLabel ? spacing(1.5) : spacing(1.75), // 12px : 14px
          fontWeight: smallLabel ? 500 : 600,
          textTransform: 'capitalize' as const,
          color: smallLabel
            ? focused
              ? palette.primary.dark
              : palette.info.dark
            : palette.primary.dark,
          marginBottom: labelMarginBottom ? labelMarginBottom : spacing(0.375),
        },
        textField: {
          background: palette.info.main,
          borderRadius: shape.borderRadius,
          color: textColor ? textColor : palette.text.primary,
          '& .MuiOutlinedInput-root': {
            '& > fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary.dark,
              borderWidth: 1,
            },
            '&.Mui-disabled > fieldset': {
              borderColor: palette.primary.dark,
              borderWidth: 1,
            },
            '&.Mui-disabled': {
              bgcolor: 'info.contrastText',
            },
          },
          '& .Mui-disabled': {
            WebkitTextFillColor: 'info.dark',
          },
          '& .MuiFormHelperText-root': {
            lineHeight: spacing(1.5),
            position: 'absolute',
            bottom: spacing(-1.875),
            color: 'error.main',
            fontSize: spacing(1.5),
            fontWeight: 500,
            ml: spacing(0.625),
          },
          'input, textarea': {
            direction: isHebrew || isTextDirectionRtl ? 'rtl' : 'ltr',
            textAlign: isHebrew || isTextDirectionRtl ? 'right' : 'left',
            ...autoFillColor,
          },
          'input[type=number]': {
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          },
          ...props.sx,
        },
        inputProps: {
          height: autoHeight
            ? 'auto'
            : large
              ? spacing(26.25)
              : big
                ? spacing(17.875)
                : medium
                  ? spacing(8.75)
                  : small
                    ? spacing(3.625)
                    : spacing(6.125),
          borderRadius: shape.borderRadius,
          borderColor: palette.error.main,
          color: textColor ? textColor : palette.text.primary,
          ...props.sx,
        },
      }),
      [
        compact,
        spacing,
        maxWidth,
        fullWidth,
        marginRight,
        marginBottom,
        smallLabel,
        focused,
        palette,
        labelMarginBottom,
        textColor,
        shape,
        isTextDirectionRtl,
        isRtl,
        isHebrew,
        autoHeight,
        large,
        big,
        medium,
        small,
        props.sx,
      ]
    );

    useEffect(() => {
      const element = internalRef.current;
      if (element) {
        if (isHebrew || isTextDirectionRtl) {
          element.dir = 'rtl';
          element.style.textAlign = 'right';
        } else {
          element.dir = 'auto';
          element.style.textAlign = 'left';
        }
      }
    }, [isHebrew, isTextDirectionRtl, props.value]);

    const handleBlur = (
      value: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (changeColorOnFocus) {
        setFocused(false);
      }
      if (blur) {
        blur(value.target.value);
      }
    };

    return (
      <Box sx={styles.container}>
        {label && (
          <Typography
            noWrap
            sx={[
              styles.label,
              ...(Array.isArray(sxLabel) ? sxLabel : sxLabel ? [sxLabel] : []),
            ]}
          >
            {label}
          </Typography>
        )}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
          }}
        >
          <TextField
            fullWidth
            multiline={big || medium || large || autoHeight}
            rows={autoHeight ? undefined : large ? 8 : big ? 5 : medium ? 2 : 1}
            minRows={autoHeight ? 1 : undefined}
            maxRows={autoHeight ? 8 : undefined}
            autoComplete='none'
            error={error}
            onFocus={handleFocus}
            onBlur={handleBlur}
            dir={isHebrew || isTextDirectionRtl ? 'rtl' : 'auto'}
            helperText={helperText !== '' && t(`${helperText}_validation_text`)}
            sx={styles.textField}
            inputProps={{ min: 0 }}
            InputProps={{
              sx: styles.inputProps,
              startAdornment: startAdornment,
              endAdornment: endAdornment,
            }}
            inputRef={view ? internalRef : mergedRef}
            color='primary'
            {...props}
          />
          {props.disabled && onClick && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                cursor: 'pointer',
                zIndex: 1,
                backgroundColor: 'transparent',
              }}
              onClick={onClick}
            />
          )}
        </Box>
      </Box>
    );
  }
);
